import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { auth, db } from '../firebase';
import { ref, push, set, query, orderByChild, equalTo, get } from 'firebase/database';
import { signOut } from 'firebase/auth';
import SEO from '../components/SEO';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'Autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';
const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */
interface Bot {
  id: string;
  businessName: string;
  botType?: string;
  status?: string;
}

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const S = {
  page: { display: 'flex', minHeight: 'calc(100vh - 64px)', background: 'var(--bg-page)', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  sidebar: (collapsed: boolean): React.CSSProperties => ({
    width: collapsed ? '72px' : '240px',
    background: 'var(--bg-sidebar)',
    borderRight: '1px solid var(--border-subtle)',
    display: 'flex', flexDirection: 'column', flexShrink: 0,
    position: 'relative', transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
  }),
  main: { flex: 1, overflow: 'auto', padding: '36px 32px', background: 'var(--bg-page)' } as React.CSSProperties,
  card: { background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--bg-element)', padding: '28px', marginBottom: '16px' } as React.CSSProperties,
  label: { display: 'block', fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600', letterSpacing: '0.06em', textTransform: 'uppercase' as const, marginBottom: '7px' },
  input: { width: '100%', padding: '10px 13px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '9px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' as const, fontFamily: 'inherit' },
  textarea: { width: '100%', padding: '10px 13px', background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '9px', color: 'var(--text-primary)', fontSize: '13px', outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const, fontFamily: 'inherit' },
  btnPrimary: { padding: '11px 24px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '9px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', width: '100%' } as React.CSSProperties,
  sidebarItem: (active: boolean, collapsed: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: collapsed ? '10px 0' : '8px 10px',
    borderRadius: '8px', cursor: 'pointer', marginBottom: '2px',
    background: active ? '#5B4FE8' : 'transparent',
    color: active ? 'var(--nav-item-active)' : 'var(--nav-item)',
    justifyContent: collapsed ? 'center' : 'flex-start',
    transition: 'all 0.1s', textDecoration: 'none',
    fontSize: '13px',
  }),
};

const BOT_ICONS: Record<string, string> = { whatsapp: '💬', facebook: '📘', website: '🌐', voice: '🎙️' };

/* ─────────────────────────────────────────────
   FAQ DATA
───────────────────────────────────────────── */
const faqs = [
  {
    q: 'Why is my bot not responding?',
    a: 'For WhatsApp bots: check your webhook is verified in Meta Developer Console and your access token hasn\'t expired. For website bots: make sure the embed script is correctly placed before the </body> tag. Check the browser console for errors.',
  },
  {
    q: 'How do I update my WhatsApp access token?',
    a: 'Access tokens expire every 60 days. Go to Meta Developer Console → Your App → WhatsApp → API Setup → Generate new token. Then in ZimChat, go to Dashboard → select your bot → Settings → paste the new token.',
  },
  {
    q: 'How do I connect my website bot?',
    a: 'In the Dashboard, select your website bot, then go to the Embed Code tab. Copy the script tag and paste it before the </body> tag on your website. It works on WordPress, Wix, Squarespace, and any HTML site.',
  },
  {
    q: 'Can I have different bots for different channels?',
    a: 'Yes! You can create separate bots for WhatsApp, your website, Facebook, and voice — each with its own flow. They all appear in your dashboard sidebar under "My Bots".',
  },
  {
    q: 'How do I load a template into my bot?',
    a: 'Open the Flow Builder for your bot and click "📋 Load template" in the top bar. A panel shows templates for that bot type. Select one and confirm to load it. Note: this replaces your current flow, so save first.',
  },
  {
    q: 'Why is "Add starter templates" giving a PERMISSION_DENIED error?',
    a: 'You need to add the templates node to your Firebase Realtime Database security rules. Go to Firebase Console → Realtime Database → Rules and add: "templates": { ".read": "auth != null", ".write": "auth != null" }',
  },
  {
    q: 'Can I upgrade my plan?',
    a: 'Yes. Go to Billing in the sidebar, choose a new plan, and complete payment. The change takes effect immediately.',
  },
  {
    q: 'How do I export conversation data?',
    a: 'In the Dashboard, select a bot and go to the Conversations tab. Use the filters to narrow down the data, then click "Export CSV". For daily stats, use the Reports tab → Download CSV.',
  },
  {
    q: 'What if a customer types something my bot doesn\'t recognise?',
    a: 'For WhatsApp bots, the bot stays silent for unrecognised messages. Add a broad trigger like "help" or use a fallback flow. For website bots, the "no-match message" is shown (configurable in Widget Settings).',
  },
  {
    q: 'How do I delete a bot?',
    a: 'In the Dashboard, select the bot, click Settings, scroll to the bottom and click "Delete bot". This is permanent and removes all flows, analytics, and conversation data.',
  },
];

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function Support() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState<any>(null);
  const [bots, setBots] = useState<Bot[]>([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<'contact' | 'faq'>('contact');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [priority, setPriority] = useState('normal');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        setName(u.displayName || '');
        setEmail(u.email || '');
        try {
          const q = query(ref(db, 'bots'), orderByChild('userId'), equalTo(u.uid));
          const snap = await get(q);
          const list: Bot[] = [];
          snap.forEach(c => { list.push({ id: c.key!, ...c.val() }); });
          setBots(list);
        } catch (e) { console.error(e); }
      }
    });
    return unsub;
  }, []);

  const handleLogout = async () => { await signOut(auth); navigate('/'); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setSubmitting(true);
    setError('');

    const ticket = {
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      priority,
      message: message.trim(),
      userId: user?.uid || null,
      status: 'open',
      createdAt: Date.now(),
    };

    let saved = false;

    // Try Firebase first
    try {
      const ticketRef = push(ref(db, 'support_tickets'));
      await set(ticketRef, { id: ticketRef.key, ...ticket });
      saved = true;
    } catch (firebaseErr) {
      console.warn('Firebase write failed, trying API fallback:', firebaseErr);
    }

    // Try API if Firebase failed or as additional notification
    if (!saved && API_BASE) {
      try {
        const res = await fetch(`${API_BASE}/api/support-ticket`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(ticket),
        });
        if (res.ok) saved = true;
      } catch (apiErr) {
        console.warn('API fallback also failed:', apiErr);
      }
    }

    // If both failed, open mailto as last resort — user still gets to send
    if (!saved) {
      const mailtoBody = `Name: ${ticket.name}\nEmail: ${ticket.email}\nPriority: ${ticket.priority}\n\n${ticket.message}`;
      const mailtoLink = `mailto:${companyEmail}?subject=${encodeURIComponent(ticket.subject)}&body=${encodeURIComponent(mailtoBody)}`;
      window.open(mailtoLink, '_blank');
      // Still show success since we opened their email client
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  const resetForm = () => {
    setSubject('');
    setMessage('');
    setPriority('normal');
    setSubmitted(false);
    setError('');
  };

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <>
      <SEO
        title={`Support | ${companyName}`}
        description={`Get help with ${companyName}. Contact support or browse frequently asked questions.`}
        keywords="support, help, FAQ, ZimChat"
        type="website"
      />
      <div style={S.page}>

        {/* ── SIDEBAR ── */}
        <div style={S.sidebar(sidebarCollapsed)}>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{ position: 'absolute', right: '-13px', top: '22px', width: '26px', height: '26px', background: '#5B4FE8', border: 'none', borderRadius: '50%', color: 'white', cursor: 'pointer', zIndex: 10, fontSize: '11px' }}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>

          {/* Logo */}
          <div style={{ padding: sidebarCollapsed ? '20px 0' : '20px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
            {!sidebarCollapsed ? (
              <>
                <img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={companyName} style={{ height: '68px', display: 'block', maxWidth: '130px', objectFit: 'contain' }} />
                <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginTop: '4px' }}>Bot Platform</div>
              </>
            ) : <div style={{ textAlign: 'center' }}><img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={companyName} style={{ height: '68px', objectFit: 'contain' }} /></div>}
          </div>

          <nav style={{ flex: 1, padding: sidebarCollapsed ? '12px 0' : '12px 8px', overflowY: 'auto' }}>

            {/* Menu */}
            {!sidebarCollapsed && <div style={{ fontSize: '9px', color: 'var(--nav-label)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '8px 8px 4px' }}>Menu</div>}
            {[
              { to: '/dashboard', icon: '📊', label: 'Dashboard' },
              { to: '/billing', icon: '💳', label: 'Billing' },
              { to: '/documentation', icon: '📚', label: 'Documentation' },
            ].map(item => (
              <Link key={item.to} to={item.to} style={{ ...S.sidebarItem(false, sidebarCollapsed), display: 'flex' }}>
                <span style={{ fontSize: '15px' }}>{item.icon}</span>
                {!sidebarCollapsed && <span>{item.label}</span>}
              </Link>
            ))}

            {/* Support tabs */}
            {!sidebarCollapsed && <div style={{ fontSize: '9px', color: 'var(--nav-label)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '14px 8px 4px' }}>Support</div>}
            {sidebarCollapsed && <div style={{ height: '10px' }} />}
            {[
              { id: 'contact' as const, icon: '📧', label: 'Contact us' },
              { id: 'faq' as const, icon: '❓', label: 'FAQ' },
            ].map(item => (
              <div
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={S.sidebarItem(activeTab === item.id, sidebarCollapsed)}
              >
                <span style={{ fontSize: '15px' }}>{item.icon}</span>
                {!sidebarCollapsed && <span>{item.label}</span>}
              </div>
            ))}

            {/* Bots */}
            {bots.length > 0 && (
              <>
                {!sidebarCollapsed && <div style={{ fontSize: '9px', color: 'var(--nav-label)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '14px 8px 4px' }}>My Bots</div>}
                {sidebarCollapsed && <div style={{ height: '10px' }} />}
                {bots.slice(0, 4).map(bot => (
                  <Link key={bot.id} to="/dashboard" style={{ ...S.sidebarItem(false, sidebarCollapsed), display: 'flex', textDecoration: 'none' }}>
                    <span style={{ fontSize: '14px' }}>{BOT_ICONS[bot.botType || 'whatsapp'] || '🤖'}</span>
                    {!sidebarCollapsed && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{bot.businessName}</span>}
                  </Link>
                ))}
              </>
            )}

            {/* Logout */}
            {!sidebarCollapsed && <div style={{ fontSize: '9px', color: 'var(--nav-label)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '14px 8px 4px' }}>Account</div>}
            {sidebarCollapsed && <div style={{ height: '10px' }} />}
            <div onClick={handleLogout} style={S.sidebarItem(false, sidebarCollapsed)}>
              <span style={{ fontSize: '15px' }}>🚪</span>
              {!sidebarCollapsed && <span>Logout</span>}
            </div>
          </nav>

          {/* User */}
          {!sidebarCollapsed && user && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ fontSize: '11px', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
            </div>
          )}
        </div>

        {/* ── MAIN ── */}
        <div style={S.main}>
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>

            {/* Page header */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ color: '#5B4FE8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Help Centre</div>
              <h1 style={{ color: 'var(--text-primary)', fontSize: '26px', fontWeight: '700', letterSpacing: '-0.02em', margin: '0 0 6px' }}>Support</h1>
              <p style={{ color: 'var(--text-faint)', fontSize: '14px', margin: 0 }}>Get help, report issues, or browse common questions.</p>
            </div>

            {/* Tab switcher */}
            <div style={{ display: 'flex', gap: '6px', marginBottom: '24px' }}>
              {[
                { id: 'contact' as const, label: '📧 Contact us' },
                { id: 'faq' as const, label: '❓ FAQ' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{
                    padding: '8px 20px', border: 'none', borderRadius: '8px', cursor: 'pointer',
                    background: activeTab === tab.id ? '#5B4FE8' : 'var(--bg-element)',
                    color: activeTab === tab.id ? 'white' : 'var(--text-muted)',
                    fontSize: '13px', fontWeight: activeTab === tab.id ? '700' : '400',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── CONTACT TAB ── */}
            {activeTab === 'contact' && (
              <>
                {submitted ? (
                  <div style={{ ...S.card, textAlign: 'center', padding: '48px 28px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                    <h2 style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Message sent!</h2>
                    <p style={{ color: 'var(--text-faint)', fontSize: '14px', marginBottom: '24px' }}>
                      Thanks for reaching out. We'll reply to <strong style={{ color: 'var(--text-secondary)' }}>{email}</strong> within 24 hours.
                      If your email client opened, please send that email too.
                    </p>
                    <button onClick={resetForm} style={{ ...S.btnPrimary, width: 'auto', padding: '10px 24px' }}>
                      Send another message
                    </button>
                  </div>
                ) : (
                  <div style={S.card}>
                    <h2 style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700', marginBottom: '4px' }}>Send us a message</h2>
                    <p style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '24px' }}>We respond within 24 hours. Business and Agency plans get priority support.</p>

                    {error && (
                      <div style={{ background: '#ef444410', border: '1px solid #ef444440', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', color: '#ef4444', fontSize: '13px' }}>
                        ⚠️ {error}
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                        <div>
                          <label style={S.label}>Name *</label>
                          <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={S.input} required />
                        </div>
                        <div>
                          <label style={S.label}>Email *</label>
                          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" style={S.input} required />
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '14px', marginBottom: '14px' }}>
                        <div>
                          <label style={S.label}>Subject *</label>
                          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="What's the issue about?" style={S.input} required />
                        </div>
                        <div>
                          <label style={S.label}>Priority</label>
                          <select value={priority} onChange={e => setPriority(e.target.value)}
                            style={{ ...S.input, appearance: 'none' }}>
                            <option value="low">Low — question</option>
                            <option value="normal">Normal</option>
                            <option value="high">High — urgent</option>
                          </select>
                        </div>
                      </div>

                      <div style={{ marginBottom: '20px' }}>
                        <label style={S.label}>Message *</label>
                        <textarea
                          value={message}
                          onChange={e => setMessage(e.target.value)}
                          rows={6}
                          placeholder="Describe your issue in detail. Include your bot name, what you were trying to do, and any error messages you saw."
                          style={S.textarea}
                          required
                        />
                      </div>

                      <button type="submit" disabled={submitting} style={{ ...S.btnPrimary, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}>
                        {submitting ? 'Sending...' : '✉️ Send message'}
                      </button>
                    </form>
                  </div>
                )}

                {/* Other contact methods */}
                <div style={S.card}>
                  <h3 style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px' }}>
                    Other ways to reach us
                  </h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                    {[
                      { icon: '📧', label: 'Email', value: companyEmail, href: `mailto:${companyEmail}` },
                      { icon: '📚', label: 'Documentation', value: 'Read the full docs', href: '/documentation' },
                      { icon: '⏰', label: 'Response time', value: 'Within 24 hours' },
                    ].map(item => (
                      <div key={item.label} style={{ background: 'var(--bg-page)', borderRadius: '10px', border: '1px solid var(--bg-element)', padding: '16px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', marginBottom: '8px' }}>{item.icon}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{item.label}</div>
                        {item.href ? (
                          item.href.startsWith('/') ? (
                            <Link to={item.href} style={{ color: '#818cf8', fontSize: '13px', textDecoration: 'none' }}>{item.value}</Link>
                          ) : (
                            <a href={item.href} style={{ color: '#818cf8', fontSize: '13px', textDecoration: 'none' }}>{item.value}</a>
                          )
                        ) : (
                          <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{item.value}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ── FAQ TAB ── */}
            {activeTab === 'faq' && (
              <>
                <div style={S.card}>
                  <h2 style={{ color: 'var(--text-primary)', fontSize: '17px', fontWeight: '700', marginBottom: '4px' }}>Frequently asked questions</h2>
                  <p style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '24px' }}>Quick answers to common problems.</p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {faqs.map((faq, i) => (
                      <div key={i} style={{ border: '1px solid var(--bg-element)', borderRadius: '10px', overflow: 'hidden' }}>
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          style={{
                            width: '100%', padding: '14px 16px', background: openFaq === i ? 'var(--bg-element)' : 'transparent',
                            border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                            alignItems: 'center', textAlign: 'left', gap: '12px',
                          }}
                        >
                          <span style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px' }}>{faq.q}</span>
                          <span style={{ color: '#5B4FE8', fontSize: '16px', flexShrink: 0, transition: 'transform 0.2s', transform: openFaq === i ? 'rotate(45deg)' : 'rotate(0)' }}>+</span>
                        </button>
                        {openFaq === i && (
                          <div style={{ padding: '0 16px 14px', background: 'var(--bg-element)' }}>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.7', margin: 0 }}>{faq.a}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ ...S.card, textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔧</div>
                  <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '15px', marginBottom: '6px' }}>Still stuck?</div>
                  <p style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '16px' }}>
                    Check the full documentation for step-by-step guides, or contact us directly.
                  </p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <Link to="/documentation" style={{ padding: '9px 20px', background: 'var(--bg-element)', color: 'var(--text-secondary)', textDecoration: 'none', borderRadius: '8px', fontSize: '13px' }}>
                      📚 View docs
                    </Link>
                    <button onClick={() => setActiveTab('contact')} style={{ padding: '9px 20px', background: '#5B4FE8', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                      📧 Contact us
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}